import { AddToCalendarButton } from "add-to-calendar-button-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState, Fragment, useRef } from "react";

import { ServiceAndProducts } from "../../ts/types";
import { getPDF, getQRCode } from "../../services/orders";
import { RootState } from "../../store";
import { BASE_URL, CALENDAR_URL, POD_URL } from "../../Routes/urls";
import {
  downloadFile,
  getClasses,
  getEventFullAddress,
  getFormattedDate,
  getFormattedTime,
  getLocalDateString,
  getLocalTimeString,
  getOrderPDFFileName,
} from "../../utils";
import classes from "./OrderConfirmation.module.css";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import Card from "../../components/Card/Card";
import InputLabel from "../../components/Inputs/InputLabel";
import Header from "../Header/Header";

const getAddToCalendarStyle = (): string => {
  const styles = [
    "--btn-shadow: none",
    "--btn-shadow-hover: none",
    "--btn-shadow-active: none",
    "--btn-border: var(--app-primary)",
    "--btn-background: var(--app-white)",
    "--btn-text: var(--app-primary)",
    "--btn-text-hover: var(--app-primary)",
  ];
  return styles.join("; ");
};

type Props = {};

const OrderConfirmation: React.FC<Props> = () => {
  const { orderId, podId } = useParams();
  const { currEvent, servicesProductsOfCurrEvent, selectedDate } = useSelector(
    (state: RootState) => state.events
  );
  const { selectedServiceProducts, order } = useSelector(
    (state: RootState) => state.order
  );
  const resourceName = useSelector(
    (state: RootState) => state.pod.resourceName
  );
  const [servicesAndProducts, setServicesAndProducts] = useState<
    ServiceAndProducts[]
  >([]);
  const imageRef = useRef<HTMLImageElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Filtering Service And Selected Products from serviceId And product ids
    const servicesAndProducts: ServiceAndProducts[] = [];
    for (let i = 0; i < selectedServiceProducts?.length; i++) {
      const currServiceAndProductId = selectedServiceProducts[i];

      const { serviceId, productIds } = currServiceAndProductId;

      const serviceOfEvent = servicesProductsOfCurrEvent.find(
        (service) => service.service.id === serviceId
      );

      if (serviceOfEvent) {
        const service = serviceOfEvent.service;
        const products = serviceOfEvent.products.filter((product) =>
          productIds.includes(product.id)
        );
        servicesAndProducts.push({ service, products });
      }
    }
    setServicesAndProducts(servicesAndProducts);
  }, [selectedServiceProducts]);

  useEffect(() => {
    if (orderId) {
      getQRCode(orderId).then((response) => {
        var imageUrl = window.URL.createObjectURL(response.data);
        if (imageRef.current) {
          imageRef.current.src = imageUrl;
        }
      });
    }
  }, [orderId]);

  const { startTime, endTime } = currEvent;

  const downloadPDFHandler = () => {
    if (orderId) {
      getPDF(orderId)
        .then((response) => {
          const fileName = getOrderPDFFileName(order);
          downloadFile(response, fileName);
        })
        .catch((error) => {
          return error;
        });
    }
  };

  const regForAnotherEventHandler = () => {
    navigate(
      `${BASE_URL}/${resourceName}/${POD_URL}/${podId}${window.location.search}`
    );
  };

  return (
    <>
      <Header />
      <div className={classes.container}>
        <div className={classes.popupContainer}>
          <div className={classes.popup}>
            <div>
              <div className={classes.icon}>
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <div>
                <div>Thank you for your Order!</div>
                <div className={classes.orderId}>Order ID - {order.code}</div>
              </div>
            </div>
          </div>
          <div className={classes.description}>
            You will receive confirmation of this ticket via email.
          </div>
        </div>
        <div className={classes.contain}>
          <Card className={classes.card}>
            <div className={classes.ticketDetails}>
              <div>
                <div className={classes.heading}>
                  <div>
                    <InputLabel
                      style={{
                        fontWeight: "500",
                        fontSize: "0.875rem",
                        color: "#8E8E8E",
                        minWidth: "10rem",
                      }}
                      label='YOU HAVE TO GO TO'
                    />
                    <div className={classes.eventName}>{currEvent.name}</div>
                  </div>
                  <div className={classes.addToCalendar}>
                    <AddToCalendarButton
                      name={currEvent.name}
                      startDate={getLocalDateString(selectedDate)}
                      startTime={getLocalTimeString(startTime)}
                      endDate={getLocalDateString(selectedDate)}
                      endTime={getLocalTimeString(endTime)}
                      timeZone='currentBrowser'
                      location={getEventFullAddress(currEvent)}
                      options={[
                        "Google",
                        "Apple",
                        "Microsoft365",
                        "Outlook.com",
                        "iCal|Download iCal (ICS)",
                      ]}
                      label='ADD TO MY CALENDAR'
                      buttonStyle='default'
                      trigger='click'
                      hideCheckmark='true'
                      hideBackground='true'
                      hideBranding='true'
                      hideRichData='true'
                      size='2|2|2'
                      styleLight={getAddToCalendarStyle()}
                    />
                  </div>
                </div>
                <div className={classes.boldLabel}>
                  You have registered for the following medical service(s)
                </div>
                <div className={classes.health}>
                  {servicesAndProducts.map((serviceAndProducts, idx) => {
                    const { service } = serviceAndProducts;
                    return (
                      <Fragment key={idx}>
                        <div className={classes.serviceName}>
                          {service.name}
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
                <div className={classes.boldLabel}>Where:</div>
                <div className={classes.address}>
                  {getEventFullAddress(currEvent)}
                </div>
                <div className={classes.boldLabel}>When:</div>
                <div className={classes.address}>
                  <div className={classes.time}>
                    {`${getFormattedDate(selectedDate)} | ${getFormattedTime(
                      startTime
                    )} - ${getFormattedTime(endTime)}`}
                  </div>
                </div>
                <div className={classes.boldLabel}>Details:</div>
                <div className={classes.health}>
                  {servicesAndProducts.map((serviceAndProducts, idx) => {
                    const { products } = serviceAndProducts;
                    return products.map((product) => (
                      <Fragment key={idx}>
                        <div className={classes.serviceName}>
                          {product.name}
                        </div>
                      </Fragment>
                    ));
                  })}
                </div>
                <div className={classes.presentTicket}>
                  Please present this ticket upon check-in
                </div>
                <div className={classes.note}>
                  Note: For some services you may be asked for your insurance
                  and identification card
                </div>
                <div className={classes.actions}>
                  <PrimaryButton
                    className={getClasses(
                      classes.action,
                      classes.actionDownload
                    )}
                    name='DOWNLOAD PDF'
                    onClick={downloadPDFHandler}
                  />
                  <PrimaryButton
                    className={classes.action}
                    name='REGISTER FOR ANOTHER EVENT'
                    onClick={regForAnotherEventHandler}
                  />
                </div>
              </div>
              <div className={classes.qrCode}>
                <img ref={imageRef} alt='QR CODE' />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default OrderConfirmation;
